import React from 'react';
import { formatCurrency } from '@/utils/formatters';
import {
  TimelineBar,
  ViolationSpans,
  TimeAxis,
  type TimelineZone,
  type ViolationSpan,
  type TimeAxisTick,
} from '@/components/config_forms/shared/help-graphics/timeline/TimelinePrimitives';

import { SegmentedControl } from '@/components/ui/segmented-control';
import { NumberInput } from '@/components/ui/number-input';
import { Switch } from '@/components/ui/switch';
import { ControlRow } from '@/components/config_forms/shared/ControlRow';
import { OTScaledNote } from '@/components/config_forms/shared/help-graphics/OTScaledNote';


export interface MealHourlyPremiumTimelineProps {
  deadlineHours: number;
  recurringDeadline: boolean;
  premiumType: 'fixed' | 'percentage';
  premiumValue: number;
  premiumBasis: 'base_rate' | 'ot_inclusive';
  ladder: Array<{
    code: string;
    name: string;
    multiplier: number;
    thresholdHours: number | null;
  }>;
  onPremiumTypeChange?: (type: 'fixed' | 'percentage') => void;
  onPremiumBasisChange?: (basis: 'base_rate' | 'ot_inclusive') => void;
  onPremiumValueChange?: (value: number | null) => void;
}

// Zone flex values per design spec §4.2
const ZONE_FLEX = {
  nonRecurring: { safe: 1.8, v1: 2.0, ellipsis: 0.8 },
  recurring: { safe: 1.8, v1: 2.5, overlap: 2.5, ellipsis: 0.8 },
} as const;

// Per spec §4.3: fixed mode uses formatCurrency (no cents when whole dollars,
// up to two decimal places otherwise); percentage mode uses integer rendering.
function buildRateLabel(
  premiumType: 'fixed' | 'percentage',
  premiumValue: number,
  premiumBasis: 'base_rate' | 'ot_inclusive',
): string {
  if (premiumType === 'fixed') {
    const formatted = `${formatCurrency(premiumValue, { showCents: !Number.isInteger(premiumValue) })}/hr`;
    return premiumBasis === 'ot_inclusive'
      ? `${formatted} × overtime rate`
      : formatted;
  }
  const pct = `${Math.round(Number(premiumValue))}%`;
  return premiumBasis === 'ot_inclusive'
    ? `${pct} × overtime rate`
    : `${pct} × hourly rate`;
}

const RateLabel: React.FC<{ text: string; tone?: 'v1' | 'v2' }> = ({ text, tone = 'v1' }) => (
  <span
    className="font-mono text-[9px] sm:text-[11px] md:text-[12px] font-semibold truncate whitespace-nowrap overflow-hidden max-w-[82px] px-1"
    style={{
      color: tone === 'v2'
        ? 'var(--destructive-text)'
        : 'var(--primary-text)',
    }}
    data-testid="rate-label"
    data-anim-audit="timeline-rate-label"
  >
    {text}
  </span>
);

function buildZones(recurringDeadline: boolean, rateLabel: string): TimelineZone[] {
  const safeFlex = recurringDeadline ? ZONE_FLEX.recurring.safe : ZONE_FLEX.nonRecurring.safe;
  const v1Flex = recurringDeadline
    ? ZONE_FLEX.recurring.v1
    : ZONE_FLEX.nonRecurring.v1 + ZONE_FLEX.nonRecurring.ellipsis;
  const overlapFlex = recurringDeadline ? ZONE_FLEX.recurring.overlap : 0;
  const ellipsisFlex = recurringDeadline ? ZONE_FLEX.recurring.ellipsis : 0;

  return [
    {
      id: 'safe',
      variant: 'safe',
      flex: safeFlex,
      content: (
        <span className="font-label text-[10px] sm:text-[12px] uppercase tracking-[0.5px] text-scenario-green/80">
          Safe
        </span>
      ),
    },
    {
      id: 'v1',
      variant: 'v1',
      flex: v1Flex,
      content: <RateLabel text={rateLabel} tone="v1" />,
    },
    {
      id: 'overlap',
      variant: 'overlap',
      flex: overlapFlex,
      content: (
        <div className="flex flex-col items-center justify-center gap-1">
          <RateLabel text={rateLabel} tone="v2" />
          <RateLabel text={rateLabel} tone="v2" />
        </div>
      ),
    },
    {
      id: 'ellipsis',
      variant: 'ellipsis',
      flex: ellipsisFlex,
      content: (
        <span className="font-mono text-[14px] text-primary-text/90 tracking-[1px]">···</span>
      ),
    },
  ];
}

function buildSpans(recurringDeadline: boolean): ViolationSpan[] {
  const single = ZONE_FLEX.nonRecurring;
  const recurring = ZONE_FLEX.recurring;
  const totalFlex = recurringDeadline
    ? recurring.safe + recurring.v1 + recurring.overlap + recurring.ellipsis
    : single.safe + single.v1 + single.ellipsis;

  const v1LeftPct = recurringDeadline
    ? (recurring.safe / totalFlex) * 100
    : (single.safe / totalFlex) * 100;
  const v2LeftPct = recurringDeadline
    ? ((recurring.safe + recurring.v1) / totalFlex) * 100
    : 100;

  return [
    {
      id: 'v2-span',
      label: 'Violation 2',
      variant: 'v2',
      leftPct: v2LeftPct,
      rightPct: 0,
    },
    {
      id: 'v1-span',
      label: 'Violation 1',
      variant: 'v1',
      leftPct: v1LeftPct,
      rightPct: 0,
    },
  ];
}

function buildTicks(
  recurringDeadline: boolean,
  deadlineHours: number,
): TimeAxisTick[] {
  const f = recurringDeadline ? ZONE_FLEX.recurring : ZONE_FLEX.nonRecurring;
  const totalFlex = Object.values(f).reduce((sum, v) => sum + v, 0);
  const deadline1Pct = (f.safe / totalFlex) * 100;

  const ticks: TimeAxisTick[] = [
    {
      id: 'start',
      label: 'Start',
      leftPct: 0,
      align: 'left',
      variant: 'label',
    },
    {
      id: 'deadline1',
      label: `${deadlineHours}h\ndeadline`,
      leftPct: deadline1Pct,
      align: 'center',
      variant: 'amber',
    },
  ];

  if (recurringDeadline) {
    const deadline2Pct = ((f.safe + f.v1) / totalFlex) * 100;
    ticks.push({
      id: 'deadline2',
      label: `${deadlineHours * 2}h\ndeadline`,
      leftPct: deadline2Pct,
      align: 'center',
      variant: 'amber',
    });
  } else {
    ticks.push({
      id: 'end',
      label: 'End',
      leftPct: 100,
      align: 'right',
      variant: 'label',
    });
  }

  return ticks;
}

export const MealHourlyPremiumTimeline: React.FC<MealHourlyPremiumTimelineProps> = ({
  deadlineHours,
  recurringDeadline,
  premiumType,
  premiumValue,
  premiumBasis,
  ladder,
  onPremiumTypeChange,
  onPremiumBasisChange,
  onPremiumValueChange,
}) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const bottomAnchorFrameRef = React.useRef<number | null>(null);
  const bottomAnchorTimeoutRef = React.useRef<number | null>(null);
  const rateLabel = buildRateLabel(premiumType, premiumValue, premiumBasis);
  const zones = buildZones(recurringDeadline, rateLabel);
  const spans = buildSpans(recurringDeadline);
  const ticks = buildTicks(recurringDeadline, deadlineHours);

  const withBottomAnchorDuringUpdate = React.useCallback((update: () => void) => {
    const clearTimers = () => {
      if (bottomAnchorFrameRef.current != null) {
        cancelAnimationFrame(bottomAnchorFrameRef.current);
        bottomAnchorFrameRef.current = null;
      }
      if (bottomAnchorTimeoutRef.current != null) {
        window.clearTimeout(bottomAnchorTimeoutRef.current);
        bottomAnchorTimeoutRef.current = null;
      }
    };

    const root = rootRef.current;
    if (!root) {
      update();
      return;
    }

    let scrollContainer: HTMLElement | null = root.parentElement;
    while (scrollContainer) {
      const style = window.getComputedStyle(scrollContainer);
      const canScrollY = /(auto|scroll)/.test(style.overflowY);
      if (canScrollY && scrollContainer.scrollHeight > scrollContainer.clientHeight) {
        break;
      }
      scrollContainer = scrollContainer.parentElement;
    }

    if (!scrollContainer) {
      update();
      return;
    }

    const distanceToBottom =
      scrollContainer.scrollHeight - scrollContainer.clientHeight - scrollContainer.scrollTop;
    const nearBottom = distanceToBottom <= 24;
    if (!nearBottom) {
      update();
      return;
    }

    update();
    clearTimers();

    const startedAt = performance.now();
    const keepBottomAnchored = () => {
      scrollContainer!.scrollTop = scrollContainer!.scrollHeight;
      if (performance.now() - startedAt < 360) {
        bottomAnchorFrameRef.current = requestAnimationFrame(keepBottomAnchored);
      } else {
        bottomAnchorFrameRef.current = null;
      }
    };

    bottomAnchorFrameRef.current = requestAnimationFrame(keepBottomAnchored);
    bottomAnchorTimeoutRef.current = window.setTimeout(() => {
      if (bottomAnchorFrameRef.current != null) {
        cancelAnimationFrame(bottomAnchorFrameRef.current);
        bottomAnchorFrameRef.current = null;
      }
      bottomAnchorTimeoutRef.current = null;
    }, 420);
  }, []);

  React.useEffect(() => () => {
    if (bottomAnchorFrameRef.current != null) {
      cancelAnimationFrame(bottomAnchorFrameRef.current);
    }
    if (bottomAnchorTimeoutRef.current != null) {
      window.clearTimeout(bottomAnchorTimeoutRef.current);
    }
  }, []);

  return (
    <div
      ref={rootRef}
      className="w-full"
      data-testid="meal-hourly-premium-timeline"
      data-anim-audit="meal-hourly-premium-timeline"
      data-anim-audit-state={`${recurringDeadline ? 'recurring' : 'single-pass'}|${premiumType}|${premiumBasis}`}
    >
      <ViolationSpans spans={spans} />

      <TimelineBar
        zones={zones}
        className="h-14"
      />

      <TimeAxis ticks={ticks} />

      <OTScaledNote visible={premiumBasis === 'ot_inclusive'} />

      {/* Control cards */}
      {onPremiumTypeChange && (
        <div className="mt-3 rounded-[0.25rem] border border-border/30 bg-surface-2/30 divide-y divide-border/20" data-anim-audit="meal-hourly-premium-controls">
          <ControlRow layout="responsive" label="Premium Type" description="Choose a flat dollar amount per hour or a percentage of your hourly rate." data-anim-audit="meal-hourly-premium-type-row">
            <SegmentedControl
              ariaLabel="Premium Type"
              options={[
                { value: 'fixed', label: 'Dollar' },
                { value: 'percentage', label: 'Percentage' },
              ]}
              value={premiumType}
              onChange={(v) => onPremiumTypeChange(v as 'fixed' | 'percentage')}
              className="w-full sm:w-auto sm:min-w-[220px] self-start sm:self-auto"
              size="sm"
            />
          </ControlRow>

          {onPremiumValueChange && (
            <ControlRow
              layout="responsive"
              label="Hourly Premium"
              description={premiumType === 'fixed'
                ? 'Dollar amount added per hour during the surcharge period.'
                : 'Percentage of your hourly rate added per hour.'}
              data-anim-audit="meal-hourly-premium-value-row"
            >
              <div className="self-start sm:self-auto">
                <NumberInput
                  aria-label="Hourly premium"
                  size="xs"
                  touchTarget
                  value={premiumValue}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    onPremiumValueChange(isNaN(val) ? null : val);
                  }}
                  unit={premiumType === 'fixed' ? '$' : '%'}
                  unitPosition={premiumType === 'fixed' ? 'left' : 'right'}
                  step={premiumType === 'fixed' ? 1 : 5}
                  minValue={0}
                />
              </div>
            </ControlRow>
          )}

          {onPremiumBasisChange && (
            <ControlRow layout="responsive" label="Scale with Overtime" description="Scale the premium by the active overtime multiplier." data-anim-audit="meal-hourly-premium-basis-row">
              <Switch
                aria-label="Scale with overtime"
                data-anim-audit="meal-hourly-premium-basis-switch"
                className="self-start sm:self-auto"
                checked={premiumBasis === 'ot_inclusive'}
                onCheckedChange={(checked) => {
                  const nextBasis = checked ? 'ot_inclusive' : 'base_rate';
                  if (nextBasis === premiumBasis) return;
                  withBottomAnchorDuringUpdate(() => onPremiumBasisChange(nextBasis));
                }}
              />
            </ControlRow>
          )}
        </div>
      )}
    </div>
  );
};
